import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { Category } from '../interfaces';

interface Sidebar {
  opened: boolean;
  collapsed: boolean;
}
@Injectable({ providedIn: 'root' })
export class SideMenuService {
  public categories$ = new BehaviorSubject<Category[]>([]);
  isCollapsable$ = new BehaviorSubject<boolean>(true);
  state$ = new BehaviorSubject<Sidebar>({
    opened: true,
    collapsed: false,
  });
  sideMenuWidth: number;

  private readonly containerWidth = 720;
  private readonly collapsedSideMenuWidth = 72;
  private readonly sidePadding = 2 * 16;
  private readonly animationTime = 350;
  private readonly collapsableWidth =
    this.containerWidth + 2 * this.collapsedSideMenuWidth + this.sidePadding;
  private sideMenuElementRef: ElementRef;

  constructor(private http: HttpClient) {
    this.setDefault();
    fromEvent(window, 'resize').subscribe(() => {
      if (!this.sideMenuWidth && window.innerWidth > this.collapsableWidth) {
        this.state$.next({ opened: true, collapsed: false });
        this.updateSideMenuWidth();
      } else {
        this.setDefault();
      }
    });
  }

  setDefault() {
    const state = {
      opened: true,
      collapsed: false,
    };
    const width = window.innerWidth;

    if (width <= this.collapsableWidth) {
      state.opened = false;
      this.isCollapsable$.next(false);
    } else if (
      width > this.collapsableWidth &&
      width < this.containerWidth + 2 * this.sideMenuWidth + this.sidePadding
    ) {
      state.collapsed = true;
      this.isCollapsable$.next(true);
    }

    this.state$.next(state);
  }

  openToggle() {
    this.state$.next({
      ...this.state$.value,
      opened: !this.state$.value.opened,
    });
  }

  collapsedToggle() {
    this.state$.next({
      ...this.state$.value,
      collapsed: !this.state$.value.collapsed,
    });
  }

  setSideMenuElementRef(elementRef: ElementRef) {
    this.sideMenuElementRef = elementRef;
    this.updateSideMenuWidth();
  }

  fetchCategories(): Observable<Category[]> {
    return this.http.get<Category[]>(`/api/categories/`);
  }

  public clearCategories(): void {
    this.categories$.next([]);
  }

  private updateSideMenuWidth() {
    setTimeout(() => {
      if (!this.sideMenuWidth) {
        this.sideMenuWidth = this.sideMenuElementRef?.nativeElement?.clientWidth;
      }
      this.setDefault();
    }, this.animationTime);
  }
}
